/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { EventStore } from '../EventStore';
import { ChangeType } from '../model/ChangeType';
import { EventDispatchManager } from '../EventDispatchManager';
import { createError, createLogger, ErrorCodes, Logger, LoggerLevel, Error, UtilFactory } from '@quickplay/foundation';
import { initializeApp } from 'firebase/app';
import {
  collection,
  doc,
  DocumentChange,
  DocumentChangeType,
  DocumentSnapshot,
  Firestore,
  FirestoreError,
  getFirestore,
  onSnapshot,
  query,
  QuerySnapshot,
  Unsubscribe,
  QueryDocumentSnapshot,
  DocumentData,
  DocumentReference,
  getDoc,
  initializeFirestore,
} from 'firebase/firestore';
import { StreamTimelineConfiguration } from '../model/StreamTimelineConfiguration';
import { DefaultEventDispatchManager } from './EventDispatchManagerImpl';
import { Player } from '@quickplay/player-interface';
import { EventInfo } from '../model/EventInfo';
import { StreamTimelineErrorCodes } from './StreamTimelineErrorCodes';

const TAG_NAME = 'FL_STREAM_TIMELINE';

export class FirebaseEventStore implements EventStore {
  private eventDispatcher: EventDispatchManager | undefined;
  private firebaseApp: any;
  private db: Firestore | undefined;
  private eventsUnsubscribe: (() => void) | undefined;
  private gameMetadataUnsubscribe: Unsubscribe | undefined;
  private logger: Logger;
  private gameInfo_: EventInfo | undefined = undefined;
  private gameMetadataDeferred_ = new UtilFactory.Deferred<Error | undefined>();
  private gameEventDeferred_ = new UtilFactory.Deferred<Error | undefined>();
  private documentDeferred = new UtilFactory.Deferred<boolean>();
  private player_: Player | undefined;

  /**
   * Creates a new instance of the default event store.
   * @param firebaseConfiguration an object containing all required properties to initialize Firebase
   * @param logger the logger to log messages
   */
  constructor(
    private readonly configuration: StreamTimelineConfiguration,
    player: Player,
    logger?: Logger,
  ) {
    this.logger = logger || createLogger(LoggerLevel.OFF, TAG_NAME);
    this.gameMetadataDeferred_ = new UtilFactory.Deferred<Error | undefined>();
    this.gameEventDeferred_ = new UtilFactory.Deferred<Error | undefined>();
    this.documentDeferred = new UtilFactory.Deferred<boolean>();

    this.eventDispatcher = new DefaultEventDispatchManager(this.configuration.spoiler, player, this.logger);
    this.player_ = player;

    this.initializeFirebase();
    this.setupPlayerListeners();
  }

  get gameInfo(): EventInfo | undefined {
    return this.gameInfo_;
  }

  /**
   * Reports a realtime update to the event dispatcher.
   * @param type the change type (ADDED, MODIFIED, DELETED)
   * @param documentId the document unique identifier
   * @param document the document
   * @private
   */
  private publishEvent(type: ChangeType, documentId: string, document: { [key: string]: any }): void {
    this.eventDispatcher?.eventReceived(type, documentId, document);
  }

  /**
   * Reports a realtime update to the event dispatcher about the game info.
   * @param document the document
   * @private
   */
  private publishGameInfo(document: EventInfo): void {
    this.gameInfo_ = document;
    this.eventDispatcher?.eventMetadata(document);
  }

  private initializeFirebase(): void {
    const defaultFirestoreSettings = { experimentalAutoDetectLongPolling: true, useFetchStreams: false };
    try {
      // initialize the Firebase app instance
      this.firebaseApp = initializeApp(this.configuration.firebaseConfig);

      // Refer: [FLPS-271]
      // Unable to fetch the documents from firebase db to show the streamline events on some TV platforms ex: Tizen 2021
      // To resolve this issue we are initializing the firestore with below `firestoresettings`.
      // If its not configured, we are using that `experimentalAutoDetectLongPolling: true` and `useFetchStreams: false` as default attribute
      // Since we are using this `initializeFirestore` it won't affect the `getFirestore` function.

      if (this.configuration.firestoreSettings) {
        initializeFirestore(this.firebaseApp, this.configuration.firestoreSettings);
      } else {
        initializeFirestore(this.firebaseApp, defaultFirestoreSettings);
      }

      // create an interface for Cloud Firestore service
      this.db = getFirestore(this.firebaseApp);
    } catch {
      this.db = undefined;
    }

    if (!this.db) {
      this.logger.warn(`Could not set Firestore`);
    }
  }

  private unsubscribeFirebaseListeners(): void {
    this.unsubscribeFromEvents();
    this.unsubscribeFromGameMetadata();
  }

  private setupPlayerListeners(): void {
    if (!this.player_) {
      return;
    }
    this.player_.subscribe('error', () => {
      this.unsubscribeFirebaseListeners();
    });
  }

  private destroyPlayerListeners(): void {
    if (!this.player_) {
      return;
    }
    this.player_.unsubscribe('error');
  }

  private eventInfoConverter = {
    toFirestore: (data: EventInfo): DocumentData => data,
    fromFirestore: (snap: QueryDocumentSnapshot<DocumentData>): EventInfo => snap.data() as EventInfo,
  };

  exists(leagueName: string, gameId: string): Promise<boolean> {
    return this.isDocumentExists(leagueName, gameId);
  }

  subscribeToEvents(leagueName: string, gameId: string, collectionName: string): Promise<Error | undefined> {
    if (!this.db) {
      this.logger.warn(`Firestore not set`);
      this.gameMetadataDeferred_.resolve(undefined);
    } else {
      this.eventsUnsubscribe = onSnapshot(
        query(collection(this.db, leagueName, gameId, collectionName)),
        (snapshot: QuerySnapshot) => {
          this.gameMetadataDeferred_.resolve(undefined);
          snapshot.docChanges().forEach((diff: DocumentChange) => {
            this.logger.debug('Document changed', diff.doc.data());

            this.publishEvent(this.getChangeType(diff.type), diff.doc.id, diff.doc.data());
          });
        },
        (error: FirestoreError) => {
          this.logger.error('Error fetching documents for stream timeline events', error);
          this.gameMetadataDeferred_.reject(this.createSessionError(error));
        },
      );
    }
    return this.gameMetadataDeferred_.promise;
  }

  subscribeToGameMetadata(leagueName: string, gameId: string): Promise<Error | undefined> {
    if (!this.db) {
      this.logger.warn(`Firestore not set`);
      this.gameEventDeferred_.resolve(undefined);
    } else {
      this.gameMetadataUnsubscribe = onSnapshot(
        doc(this.db, leagueName, gameId).withConverter(this.eventInfoConverter),
        (documentSnapshot: DocumentSnapshot<EventInfo>) => {
          const eventInfo = documentSnapshot.data();
          if (eventInfo) {
            this.publishGameInfo(eventInfo);
          } else {
            this.logger.warn('scheduledStart not available in metadata');
          }
          this.gameEventDeferred_.resolve(undefined);
        },
        (error: FirestoreError) => {
          this.logger.error('Error subscribing to game info', error);
          this.gameEventDeferred_.reject(this.createSessionError(error));
        },
      );
    }
    return this.gameEventDeferred_.promise;
  }

  /**
   * Returns the Firebase change type mapped to a ChangeType enumeration.
   * @param type
   * @private
   */
  private getChangeType(type: DocumentChangeType): ChangeType {
    switch (type) {
      case 'added':
        return ChangeType.ADDED;
      case 'modified':
        return ChangeType.MODIFIED;
      case 'removed':
        return ChangeType.DELETED;
      default:
        return ChangeType.UNKNOWN;
    }
  }

  private isDocumentExists = async (leagueName: string, gameId: string): Promise<boolean> => {
    if (!this.db) {
      this.logger.warn(`Firestore not set`);
      this.documentDeferred.resolve(false);
    } else {
      const document: DocumentReference<DocumentData> = doc(this.db, leagueName, gameId);
      const documentSnapshot = await getDoc(document);
      if (!documentSnapshot.exists()) {
        this.documentDeferred.resolve(false);
      }
      this.documentDeferred.resolve(true);
    }
    return this.documentDeferred.promise;
  };

  private createSessionError = (error: FirestoreError): Error => {
    return createError(
      StreamTimelineErrorCodes.SESSION_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
      error.message,
      undefined,
      JSON.stringify(error),
    );
  };

  unsubscribeFromEvents(): void {
    if (this.eventsUnsubscribe) {
      this.eventsUnsubscribe();
      this.eventsUnsubscribe = undefined;
    }
  }

  unsubscribeFromGameMetadata(): void {
    if (this.gameMetadataUnsubscribe) {
      this.gameMetadataUnsubscribe();
      this.gameMetadataUnsubscribe = undefined;
    }
  }

  updatePlayer(player: Player): void {
    this.destroyPlayerListeners();
    this.player_ = player;
    this.eventDispatcher?.updatePlayer(player);
  }
}
